import { PlanogramDetailSchema } from "@labeling-tools/api/atlas";
import { getPlanoDimension } from "../../utils/plano-related.utils";
import { OneLabelBox } from "./one-label-box/one-label-box.comp";
import { useActivePlanoProductInfoStore } from "../use-active-plano-product-info-store";
import { useRef, useLayoutEffect, useState, useEffect } from "react";
import { Loader } from "@mantine/core";

interface Props {
  planogram: PlanogramDetailSchema;
  callback?: (product_id: string | null) => void;
  selectedProductIds?: string[];
  warnedProductIds?: string[];
  highlightedProductIds?: string[];
  currentScale?: number;
  imageUrlOverrides?: Record<string, string>;
  tooltipImageUrlOverrides?: Record<string, string>;
  backgroundImageUrl?: string | null;
  searchMatchedIds?: Set<string>;
  /** Nonce that re-pulses framed selected boxes when it changes (opt-in). */
  pulseNonce?: number;
  /** product_ids that should pulse when `pulseNonce` changes. */
  pulseProductIds?: string[];
}

export const PlanoLabelBoxes = (props: Props) => {
  const {
    planogram,
    callback,
    selectedProductIds,
    warnedProductIds,
    highlightedProductIds,
    currentScale,
    imageUrlOverrides,
    tooltipImageUrlOverrides,
    backgroundImageUrl,
    searchMatchedIds,
    pulseNonce,
    pulseProductIds,
  } = props;
  const planogramProducts = planogram?.products ?? [];
  const activePlanogramProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.planogram_product_id,
  );

  const planoDimension = getPlanoDimension(planogramProducts);
  const showImage = true;

  const [bgLoaded, setBgLoaded] = useState(false);
  const [bgError, setBgError] = useState(false);
  // Reset when the background image URL changes
  useEffect(() => {
    setBgLoaded(false);
    setBgError(false);
  }, [backgroundImageUrl]);

  const backgroundMode = !!backgroundImageUrl && !bgError;

  // Prefetch product images so they're cached before the user zooms in.
  // In background mode this still runs so thumbnails load progressively behind the stitched image.
  useEffect(() => {
    const urls = new Set<string>();
    for (const p of planogramProducts) {
      const thumbUrl = imageUrlOverrides?.[p.id] ?? p.image_url;
      if (thumbUrl) urls.add(thumbUrl);
      const tooltipUrl = tooltipImageUrlOverrides?.[p.id];
      if (tooltipUrl) urls.add(tooltipUrl);
    }
    urls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, [planogramProducts, imageUrlOverrides, tooltipImageUrlOverrides]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [contentSize, setContentSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !planoDimension.w || !planoDimension.h) return;

    const planoAR = planoDimension.w / planoDimension.h;

    const update = () => {
      const cw = el.offsetWidth;
      const ch = el.offsetHeight;
      if (!cw || !ch) return;
      const containerAR = cw / ch;
      if (planoAR >= containerAR) {
        setContentSize({ width: cw, height: cw / planoAR });
      } else {
        setContentSize({ width: ch * planoAR, height: ch });
      }
    };

    const observer = new ResizeObserver(update);
    observer.observe(el);
    update();
    return () => observer.disconnect();
  }, [planoDimension.w, planoDimension.h]);

  return (
    <div
      ref={containerRef}
      className="flex items-center justify-center size-full"
    >
      <div
        className="relative"
        style={
          contentSize
            ? { width: contentSize.width, height: contentSize.height }
            : {
                width: "100%",
                aspectRatio: `${planoDimension.w} / ${planoDimension.h}`,
              }
        }
      >
        {/* Pre-stitched background image — aligns with overlay boxes via same aspect-ratio math */}
        {backgroundImageUrl && !bgError && (
          <img
            src={backgroundImageUrl}
            alt=""
            draggable={false}
            className="absolute inset-0 size-full pointer-events-none select-none"
            onLoad={() => setBgLoaded(true)}
            onError={() => setBgError(true)}
          />
        )}
        {backgroundImageUrl && !bgError && !bgLoaded && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Loader size="sm" />
          </div>
        )}

        {planogramProducts.map((planogramProduct) => {
          return (
            <OneLabelBox
              key={planogramProduct.id}
              planogramProduct={planogramProduct}
              planoDimension={planoDimension}
              isSelected={
                selectedProductIds
                  ? selectedProductIds.includes(planogramProduct.product_id)
                  : activePlanogramProductId === planogramProduct.id
              }
              isWarned={warnedProductIds?.includes(planogramProduct.product_id)}
              isHighlighted={highlightedProductIds?.includes(
                planogramProduct.product_id,
              )}
              isSearchMatched={searchMatchedIds?.has(planogramProduct.id)}
              callback={callback}
              showImage={showImage}
              hideProductImage={backgroundMode}
              currentScale={currentScale}
              imageUrlOverride={imageUrlOverrides?.[planogramProduct.id]}
              tooltipImageUrlOverride={
                tooltipImageUrlOverrides?.[planogramProduct.id]
              }
              pulseKey={
                pulseProductIds?.includes(planogramProduct.product_id)
                  ? pulseNonce
                  : 0
              }
            />
          );
        })}
      </div>
    </div>
  );
};
